import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, RefreshCw, Info, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import Navbar from "../components/Landing/Navbar";
const ageGroups = [
  { value: "below60", label: "Below 60 years" },
  { value: "60to80", label: "60 - 80 years" },
  { value: "above80", label: "Above 80 years" }
];

const employerTypes = [
  "Private Sector",
  "Government",
  "Public Sector",
  "Self-Employed",
  "Other"
];

const taxDeductions = [
  { name: "Section 80C", max: 150000, description: "Investments in PPF, ELSS, NSC, etc." },
  { name: "Section 80D", max: 75000, description: "Medical insurance premiums" },
  { name: "HRA", max: 0, description: "House Rent Allowance (actual calculation)" },
  { name: "Section 24(b)", max: 200000, description: "Home loan interest" },
  { name: "Standard Deduction", max: 50000, description: "Standard deduction for salaried" }
];

export default function IncomeTaxCalculator() {
  const [income, setIncome] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [employerType, setEmployerType] = useState(employerTypes[0]);
  const [regime, setRegime] = useState("new");
  const [taxResult, setTaxResult] = useState(null);
  const [activeDeductions, setActiveDeductions] = useState([]);
  const [showDeductions, setShowDeductions] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [oldRegimeResult, setOldRegimeResult] = useState(null);
  const [newRegimeResult, setNewRegimeResult] = useState(null);

  // Reset form completely
  const resetForm = () => {
    setIncome("");
    setAgeGroup("");
    setEmployerType(employerTypes[0]);
    setRegime("new");
    setTaxResult(null);
    setActiveDeductions([]);
    setCompareMode(false);
  };

  // Calculate tax based on regime
  const calculateTaxForRegime = (regimeType, incomeValue, deductions = 0) => {
    const annualIncome = parseFloat(incomeValue) - deductions;
    if (isNaN(annualIncome)) return null;

    let tax = 0;

    if (regimeType === "new") {
      // New Regime Slabs FY 2024-25
      const slabs = [
        { min: 0, max: 300000, rate: 0 },
        { min: 300001, max: 600000, rate: 0.05 },
        { min: 600001, max: 900000, rate: 0.10 },
        { min: 900001, max: 1200000, rate: 0.15 },
        { min: 1200001, max: 1500000, rate: 0.20 },
        { min: 1500001, max: Infinity, rate: 0.30 },
      ];

      slabs.forEach(slab => {
        if (annualIncome > slab.min) {
          const taxable = Math.min(slab.max, annualIncome) - slab.min;
          tax += taxable * slab.rate;
        }
      });
    } else {
      // Old Regime Slabs FY 2024-25 with deductions
      let exemptionLimit = 250000;
      if (ageGroup === "60to80") exemptionLimit = 300000;
      if (ageGroup === "above80") exemptionLimit = 500000;

      if (annualIncome <= exemptionLimit) {
        tax = 0;
      } else if (annualIncome <= 500000) {
        tax = (annualIncome - exemptionLimit) * 0.05;
      } else if (annualIncome <= 1000000) {
        tax = (500000 - exemptionLimit) * 0.05 + (annualIncome - 500000) * 0.20;
      } else {
        tax = (500000 - exemptionLimit) * 0.05 + 500000 * 0.20 + (annualIncome - 1000000) * 0.30;
      }
    }

    // Health & Education Cess 4%
    const cess = tax * 0.04;
    const totalTax = Math.round(tax + cess);

    return {
      tax: Math.round(tax),
      cess: Math.round(cess),
      totalTax,
      taxableIncome: annualIncome,
      regime: regimeType
    };
  };

  // Main calculation function
  const calculateTax = () => {
    const annualIncome = parseFloat(income);
    if (isNaN(annualIncome) || annualIncome <= 0 || !ageGroup) {
      alert("Please enter valid income and select your age group");
      return;
    }

    // Calculate total deductions
    const totalDeductions = activeDeductions.reduce((sum, deduction) => {
      return sum + (deduction.amount || 0);
    }, 0);

    // Calculate for current regime
    const result = calculateTaxForRegime(regime, income, totalDeductions);
    setTaxResult(result);

    // If in compare mode, calculate for both regimes
    if (compareMode) {
      const oldResult = calculateTaxForRegime("old", income, totalDeductions);
      const newResult = calculateTaxForRegime("new", income, 0); // New regime has limited deductions
      setOldRegimeResult(oldResult);
      setNewRegimeResult(newResult);
    }
  };

  // Toggle deduction
  const toggleDeduction = (deduction) => {
    setActiveDeductions(prev => {
      const exists = prev.find(d => d.name === deduction.name);
      if (exists) {
        return prev.filter(d => d.name !== deduction.name);
      } else {
        return [...prev, { ...deduction, amount: deduction.max }];
      }
    });
  };

  // Update deduction amount
  const updateDeductionAmount = (name, value) => {
    setActiveDeductions(prev =>
      prev.map(d => 
        d.name === name ? { ...d, amount: Math.min(parseFloat(value) || 0, d.max || Infinity) } : d
      )
    );
  };

  // Effect to recalculate when inputs change
  useEffect(() => {
    if (income && ageGroup) {
      calculateTax();
    }
  }, [income, ageGroup, regime, activeDeductions, compareMode]);

  return (
    <>
      <Navbar/>
   
    <div className="min-h-screen bg-gradient-to-br from-gray-200 to-gray-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
       

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Input Panel */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden lg:col-span-2"
          >
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-cyan-600 flex items-center gap-2">
                <Calculator className="text-cyan-600" /> Tax Information
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Income Input */}
              <div>
                <label className="block text-sm font-medium text-cyan-600 mb-1">
                  Gross Annual Income (₹)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                    ₹
                  </span>
                  <input
                    type="number"
                    placeholder="e.g., 12,00,000"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-500 text-sm">
                    INR
                  </span>
                </div>
              </div>

              {/* Age Group */}
              <div>
                <label className="block text-sm font-medium text-cyan-600 mb-1">
                  Age Group
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {ageGroups.map((age) => (
                    <button
                      key={age.value}
                      onClick={() => setAgeGroup(age.value)}
                      className={`py-2 px-4 rounded-md border transition-colors ${
                        ageGroup === age.value
                          ? "bg-cyan-50 border-cyan-500 text-cyan-700"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {age.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Employer Type */}
              <div>
                <label className="block text-sm font-medium text-cyan-600 mb-1">
                  Employer Type
                </label>
                <select
                  value={employerType}
                  onChange={(e) => setEmployerType(e.target.value)}
                  className="block w-full py-3 px-4 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {employerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tax Regime */}
              <div>
                <label className="block text-sm font-medium text-cyan-600 mb-1">
                  Tax Regime
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setRegime("new")}
                    className={`flex items-center justify-center py-2 px-4 rounded-md border transition-colors ${
                      regime === "new"
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    New Regime
                  </button>
                  <button
                    onClick={() => setRegime("old")}
                    className={`flex items-center justify-center py-2 px-4 rounded-md border transition-colors ${
                      regime === "old"
                        ? "bg-purple-50 border-purple-500 text-purple-700"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Old Regime
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {regime === "new"
                    ? "New regime has lower rates but fewer deductions"
                    : "Old regime has higher rates but more deductions"}
                </p>
              </div>

              {/* Compare Mode */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="compareMode"
                  checked={compareMode}
                  onChange={() => setCompareMode(!compareMode)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="compareMode" className="text-sm font-medium text-cyan-600">
                  Compare both tax regimes
                </label>
              </div>

              {/* Deductions Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-cyan-600">
                    Deductions (Old Regime Only)
                  </label>
                  <button
                    onClick={() => setShowDeductions(!showDeductions)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showDeductions ? "Hide" : "Show"} deductions
                  </button>
                </div>

                <AnimatePresence>
                  {showDeductions && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-3 mt-2">
                        {taxDeductions.map((deduction) => (
                          <div
                            key={deduction.name}
                            className={`p-3 border rounded-md transition-colors ${
                              activeDeductions.some(d => d.name === deduction.name)
                                ? "border-blue-300 bg-blue-50"
                                : "border-gray-200"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={activeDeductions.some(d => d.name === deduction.name)}
                                  onChange={() => toggleDeduction(deduction)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div>
                                  <p className="text-sm font-medium text-cyan-600">
                                    {deduction.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {deduction.description}
                                  </p>
                                </div>
                              </div>
                              {deduction.max > 0 && (
                                <span className="text-xs text-gray-500">
                                  Max: ₹{deduction.max.toLocaleString()}
                                </span>
                              )}
                            </div>

                            {activeDeductions.some(d => d.name === deduction.name) && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="mt-2"
                              >
                                <label className="text-xs text-gray-500 block mb-1">
                                  Amount (₹)
                                </label>
                                <input
                                  type="number"
                                  value={
                                    activeDeductions.find(d => d.name === deduction.name)?.amount ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    updateDeductionAmount(deduction.name, e.target.value)
                                  }
                                  max={deduction.max || undefined}
                                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                              </motion.div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 pt-4">
                <button
                  onClick={resetForm}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-cyan-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <RefreshCw className="-ml-1 mr-2 h-4 w-4" />
                  Reset
                </button>
                <div className="flex-1" />
                <button
                  onClick={calculateTax}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-cyan-600 to-cyan-400 hover:from-cyan-700 hover:to-cyan-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                >
                  Calculate Tax
                  <ArrowRight className="ml-2 -mr-1 h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Results Panel */}
          <div className="space-y-6">
            {/* Main Result Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div
                className={`p-6 border-b ${
                  taxResult
                    ? regime === "new"
                      ? "border-green-200 bg-green-50"
                      : "border-purple-200 bg-purple-50"
                    : "border-gray-200"
                }`}
              >
                <h2 className="text-xl font-semibold text-cyan-600 flex items-center gap-2">
                  <Calculator className="text-cyan-600" /> Tax Summary
                </h2>
              </div>

              <div className="p-6">
                {taxResult ? (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">Taxable Income</span>
                      <span className="text-lg font-semibold">
                        ₹{taxResult.taxableIncome.toLocaleString()}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500">Income Tax</span>
                        <span className="font-medium">
                          ₹{taxResult.tax.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500">
                          Health & Education Cess (4%)
                        </span>
                        <span className="font-medium">
                          ₹{taxResult.cess.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-medium text-cyan-600">
                          Total Tax Liability
                        </span>
                        <span className="text-xl font-bold text-blue-600">
                          ₹{taxResult.totalTax.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {regime === "old" && activeDeductions.length > 0 && (
                      <div className="bg-blue-50 p-3 rounded-md">
                        <p className="text-sm text-blue-800">
                          You saved ₹
                          {(
                            calculateTaxForRegime("old", income, 0)?.totalTax - taxResult.totalTax
                          ).toLocaleString()}{" "}
                          using deductions
                        </p>
                      </div>
                    )}

                    <div className="pt-4">
                      <p className="text-xs text-gray-500">
                        Based on {regime === "new" ? "New" : "Old"} Tax Regime for FY 2024-25
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calculator className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No calculation yet
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Enter your details and click "Calculate Tax" to see results.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Comparison Card - Only visible in compare mode */}
            {compareMode && oldRegimeResult && newRegimeResult && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-6 border-b border-yellow-200 bg-yellow-50">
                  <h2 className="text-xl font-semibold text-cyan-600 flex items-center gap-2">
                    <Info className="text-yellow-600" /> Regime Comparison
                  </h2>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-3 rounded-md ${
                        oldRegimeResult.totalTax < newRegimeResult.totalTax
                          ? "bg-green-50 border border-green-200"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <p className="text-xs font-medium text-gray-500">Old Regime</p>
                      <p className="text-lg font-semibold">
                        ₹{oldRegimeResult.totalTax.toLocaleString()}
                      </p>
                      {oldRegimeResult.totalTax < newRegimeResult.totalTax && (
                        <p className="text-xs text-green-600 mt-1 flex items-center">
                          <TrendingDown className="mr-1 h-4 w-4" /> Better for you
                        </p>
                      )}
                    </div>

                    <div
                      className={`p-3 rounded-md ${
                        newRegimeResult.totalTax < oldRegimeResult.totalTax
                          ? "bg-green-50 border border-green-200"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <p className="text-xs font-medium text-gray-500">New Regime</p>
                      <p className="text-lg font-semibold">
                        ₹{newRegimeResult.totalTax.toLocaleString()}
                      </p>
                      {newRegimeResult.totalTax < oldRegimeResult.totalTax && (
                        <p className="text-xs text-green-600 mt-1 flex items-center">
                          <TrendingUp className="mr-1 h-4 w-4" /> Better for you
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-xs text-gray-500">
                      Difference: ₹
                      {Math.abs(
                        oldRegimeResult.totalTax - newRegimeResult.totalTax
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Tax Savings Tips */}
            {taxResult && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-6 border-b border-blue-200 bg-blue-50">
                  <h2 className="text-xl font-semibold text-cyan-600 flex items-center gap-2">
                    <Info className="text-blue-600" /> Tax Saving Tips
                  </h2>
                </div>

                <div className="p-6 space-y-4">
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 flex-shrink-0 text-blue-500">•</span>
                      <span>
                        Consider maximizing your <strong>Section 80C</strong> investments (up to ₹1.5
                        lakh)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 flex-shrink-0 text-blue-500">•</span>
                      <span>
                        Health insurance premiums under <strong>Section 80D</strong> can save up to ₹75,000
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 flex-shrink-0 text-blue-500">•</span>
                      <span>
                        Home loan interest under <strong>Section 24(b)</strong> can save up to ₹2 lakh
                      </span>
                    </li>
                    {regime === "new" && (
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 flex-shrink-0 text-blue-500">•</span>
                        <span>
                          New regime has lower rates but fewer deductions. Compare both regimes.
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
     </>
  );
}